import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { quotes as quotesApi, notifications as notifApi, business as bizApi } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { IDS } from "@/constants/testIds";
import {
  FileText, CheckCircle2, XCircle, Clock, TrendingUp, Send, Bell, ArrowRight, Mic,
  Trophy, Timer, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

const StatCard = ({ label, value, sub, icon: Icon, tone = "blue", testId }) => {
  const toneMap = {
    blue: "bg-[#EFF6FF] text-[#2563EB]",
    green: "bg-[#ECFDF5] text-[#10B981]",
    amber: "bg-[#FFF7ED] text-[#EA580C]",
    slate: "bg-slate-100 text-[#334155]",
  };
  return (
    <div data-testid={testId}
         className="rounded-lg border border-slate-200 bg-white p-5 hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">{label}</div>
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${toneMap[tone]}`}>
          <Icon size={18} />
        </div>
      </div>
      <div className="mt-3 text-3xl font-black tracking-tight text-[#001F3F]"
           style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-[#64748B]">{sub}</div>}
    </div>
  );
};

const StatusBadge = ({ s }) => {
  const map = {
    draft: "bg-slate-100 text-[#64748B]",
    sent: "bg-blue-50 text-[#2563EB]",
    viewed: "bg-indigo-50 text-[#4F46E5]",
    accepted: "bg-green-50 text-[#10B981]",
    declined: "bg-red-50 text-[#EF4444]",
    modification_requested: "bg-amber-50 text-[#EA580C]",
  };
  const label = { modification_requested: "Modification requested" }[s] || s;
  return (
    <span className={`px-2.5 py-1 text-[11px] rounded-full uppercase tracking-[0.1em] font-semibold ${map[s] || "bg-slate-100 text-slate-500"}`}>
      {label}
    </span>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [recent, setRecent] = useState([]);
  const [biz, setBiz] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    bizApi.get()
      .then(({ data }) => {
        if (!data?.onboarded) return nav("/onboarding");
        setBiz(data);
      })
      .catch(() => nav("/onboarding"));
    quotesApi.stats().then(({ data }) => setStats(data));
    quotesApi.list().then(({ data }) => setRecent((data || []).slice(0, 6)));
    notifApi.list().then(({ data }) => setNotifs((data || []).slice(0, 6)));
  }, [nav]);

  const currency = biz?.currency || "USD";
  const pending = (stats.sent || 0) + (stats.modification_requested || 0);

  return (
    <div data-testid={IDS.dashboard.page} className="p-5 sm:p-8 max-w-7xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">Dashboard</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-[#001F3F]"
              style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="mt-2 text-sm text-[#64748B]">
            Here&apos;s what&apos;s happening at {biz?.name || "your business"} today.
          </p>
        </div>
        <Button onClick={() => nav("/quotes/new")}
                className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 px-5">
          <Mic size={16} className="mr-2" /> New Quote
        </Button>
      </div>

      <section className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId={IDS.dashboard.statTotal} label="Total quotes" value={stats.total || 0} icon={FileText} tone="blue" />
        <StatCard testId={IDS.dashboard.statAccepted} label="Accepted" value={stats.accepted || 0} icon={CheckCircle2} tone="green"
                  sub={`${stats.declined || 0} declined`} />
        <StatCard testId={IDS.dashboard.statPending} label="Pending" value={pending} icon={Clock} tone="amber"
                  sub={`${stats.modification_requested || 0} mod. requested`} />
        <StatCard testId={IDS.dashboard.statRevenue} label={`Revenue (${currency})`}
                  value={money(stats.revenue || 0, currency)}
                  sub="From accepted quotes" icon={TrendingUp} tone="blue" />
      </section>

      {/* Analytics row */}
      <section className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          testId="dashboard-stat-win-rate"
          label="Win rate"
          value={`${stats.win_rate ?? 0}%`}
          sub={`${stats.accepted || 0} of ${(stats.accepted || 0) + (stats.declined || 0)} decided`}
          icon={Trophy}
          tone="green"
        />
        <StatCard
          testId="dashboard-stat-avg-time"
          label="Avg time to accept"
          value={stats.avg_time_to_accept_hours ? `${stats.avg_time_to_accept_hours}h` : "—"}
          sub="From sent to accepted"
          icon={Timer}
          tone="blue"
        />
        <StatCard
          testId="dashboard-stat-deposits"
          label={`Deposits collected (${currency})`}
          value={money(stats.deposits_collected || 0, currency)}
          sub={`${stats.deposits_count || 0} paid`}
          icon={Wallet}
          tone="amber"
        />
      </section>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div data-testid={IDS.dashboard.recentQuotes}
             className="lg:col-span-2 rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="text-lg font-bold tracking-tight text-[#001F3F]"
                style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
              Recent quotes
            </h2>
            <button onClick={() => nav("/quotes")}
                    className="text-sm text-[#2563EB] hover:underline flex items-center gap-1">
              See all <ArrowRight size={14} />
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {recent.length === 0 && (
              <div className="p-8 text-center text-sm text-[#64748B]">
                No quotes yet.{" "}
                <button onClick={() => nav("/quotes/new")} className="text-[#2563EB] font-semibold hover:underline">
                  Create your first
                </button>
              </div>
            )}
            {recent.map((q) => (
              <button
                key={q.quote_id}
                data-testid={IDS.quotes.row(q.quote_id)}
                onClick={() => nav(`/quotes/${q.quote_id}`)}
                className="w-full text-left px-5 py-4 hover:bg-slate-50 transition-colors duration-200 flex items-center justify-between gap-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-[#64748B]">{q.quote_number} · v{q.version}</span>
                    <StatusBadge s={q.status} />
                  </div>
                  <div className="mt-1 text-sm font-semibold text-[#001F3F] truncate">{q.title}</div>
                  <div className="text-xs text-[#64748B] truncate">{q.customer_name || "No customer name"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-[#001F3F] tabular-nums">{money(q.total, q.currency)}</div>
                  <div className="text-xs text-[#64748B]">
                    {q.created_at ? format(new Date(q.created_at), "MMM d") : ""}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div id="notifications" className="rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="text-lg font-bold tracking-tight text-[#001F3F] flex items-center gap-2"
                style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
              <Bell size={16} className="text-[#2563EB]" /> Activity
            </h2>
            <button onClick={() => nav("/notifications")}
                    className="text-sm text-[#2563EB] hover:underline">View all</button>
          </div>
          <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
            {notifs.length === 0 && (
              <div className="p-6 text-center text-sm text-[#64748B]">No activity yet</div>
            )}
            {notifs.map((n) => {
              const Icon =
                n.type === "quote_accepted" ? CheckCircle2 :
                n.type === "quote_declined" ? XCircle :
                n.type === "quote_modification_requested" ? Clock : Send;
              const iconCls =
                n.type === "quote_accepted" ? "bg-green-50 text-[#10B981]" :
                n.type === "quote_declined" ? "bg-red-50 text-[#EF4444]" :
                n.type === "quote_modification_requested" ? "bg-amber-50 text-[#EA580C]" :
                "bg-blue-50 text-[#2563EB]";
              return (
                <button
                  key={n.notification_id}
                  onClick={() => nav(`/quotes/${n.quote_id}`)}
                  className="w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50 transition-colors"
                >
                  <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${iconCls}`}>
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#001F3F]">{n.message}</div>
                    <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-[0.1em]">
                      {n.created_at ? format(new Date(n.created_at), "MMM d, HH:mm") : ""}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
